import React from 'react';
import {Tag as PeacockTag} from '@pepsico-ds/ui';
import {TAG_COLOR} from '@/utils/constants/colors';
import {SIZE} from '@/utils/constants/globalStyle';
import PropTypes from 'prop-types';
/**
 * Tag component renders a tag with specified properties.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.text - The text to be displayed inside the tag.
 * @param {boolean} [props.isRemovable=false] - Whether the tag is removable; default is false.
 * @param {boolean} props.isCopyable - Whether the text is copyable; default is false.
 * @param {('Cyan-500'|'Cyan-700'|'Gray-100'|'Gray-500'|'Green-150'|'Green-600'|'Orange-150'|'Orange-400'|'Red-150'|'Red-600'|'Yellow-150'|'Yellow-400'|'Auxiliary-300'|'Auxiliary-600')} [props.color='Auxiliary-600'] props.color - The color class name for the tag.
 * @param {string} [props.id=''] - Optional additional id to apply to the tag.
 * @param {Function} [props.onRemove] - Function to handle remove events.
 * @param {string} [props.size=SIZE.LARGE] - The size of the tag.
 * @param {Function} [props.onClick] - Function to handle click events.
 * @returns {JSX.Element} The rendered Tag component.
 */

const Tag = ({
  text,
  isRemovable = false,
  isCopyable = false,
  color = TAG_COLOR.AUXILIARY_600,
  onRemove = () => {},
  id,
  size = SIZE.LARGE,
  onClick = () => {}
}) => {
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <button
      onClick={onClick}
      onKeyDown={handleKeyDown}
      style={{
        cursor: 'pointer',
        border: 'none',
        background: 'none',
        padding: 0
      }}>
      <PeacockTag
        id={`tag` + (id ? `_${id}` : '')}
        data-testid={`tag` + (id ? `_${id}` : '')}
        text={text}
        isCopyable={isCopyable}
        color={color}
        isRemovable={isRemovable}
        onRemove={onRemove}
        size={size}
      />
    </button>
  );
};

Tag.propTypes = {
  text: PropTypes.string.isRequired,
  isRemovable: PropTypes.bool,
  isCopyable: PropTypes.bool,
  color: PropTypes.oneOf([
    'Cyan-500',
    'Cyan-700',
    'Gray-100',
    'Gray-500',
    'Green-150',
    'Green-600',
    'Orange-150',
    'Orange-400',
    'Red-150',
    'Red-600',
    'Yellow-150',
    'Yellow-400',
    'Auxiliary-300',
    'Auxiliary-600'
  ]),
  id: PropTypes.string,
  onRemove: PropTypes.func,
  size: PropTypes.string,
  onClick: PropTypes.func
};

export default React.memo(Tag);
